import {
  getOpacityForReportMarker,
  getReportMapVisibilityHours,
} from 'features/mapping/icons/icons';
import {
  ReportIconFov,
  ReportIconJustCamera,
  ReportIconNoCamera,
} from 'features/mapping/icons';
import { Report, ReportPhoto } from 'features/reports/report.types';
import { Feature, FeatureCollection, Geometry } from 'geojson';
import { useMemo } from 'react';
import { Source } from 'react-map-gl';
import { Layer, LayerProps } from 'shared/map-exports';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles/constants';
import { MapboxFeature } from 'features/mapping/mapping.types';
import { history } from 'shared/history';
import { Location } from 'history';
import {
  GeoEventType,
  ReportableGeoEventType,
} from 'features/geoEvents/geoEvent.types';
import {
  getReportDetailPath,
  isRingReport,
} from 'features/reports/reports.utils';
import { hasValidCoordinates } from 'shared/utils';
import { ReportObfuscationLayer } from 'features/mapping/components/layers/ReportObfuscationLayer';

export type ReportsLayerProps = {
  reports: (Report | ReportPhoto)[];
  isFadable?: boolean;
  interactive?: boolean;
  // Incident context; when omitted, the all-incidents photos view lookbacks apply.
  geoEventType?: GeoEventType;
};

export type ReportFeatureProperties = {
  id: number;
  geoEventId: number;
  opacity: number;
  azimuth: number;
  point_count?: number;
  point_count_abbreviated?: number;
};

const REPORTS_SOURCE_ID = 'reports';
export const REPORTS_ICONS_LAYER_ID = 'report-icons';
export const REPORTS_ICONS_FULL_LAYER_ID = 'report-icons-full';
const REPORTS_VIEWSHED_LAYER_ID = 'report-viewsheds-layer';
const REPORTS_OBFUSCATION_LAYER_ID = 'reports-layer-obfuscation';

const REPORTS_ICONS_MINZOOM = 10;
const REPORTS_ICONS_SIMPLE_ZOOM_THRESHOLD = 12;
const REPORTS_ICONS_SIMPLE_SIZE = 0.5;

const iconSimpleStyle: LayerProps = {
  type: 'symbol',
  minzoom: REPORTS_ICONS_MINZOOM,
  maxzoom: REPORTS_ICONS_SIMPLE_ZOOM_THRESHOLD,
  layout: {
    'icon-size': REPORTS_ICONS_SIMPLE_SIZE,
    'icon-image': ReportIconNoCamera.name,
    'icon-allow-overlap': true,
    'icon-ignore-placement': false,
  },
  paint: {
    'icon-opacity': ['get', 'opacity'],
  },
};

const iconFullStyle: LayerProps = {
  type: 'symbol',
  minzoom: REPORTS_ICONS_SIMPLE_ZOOM_THRESHOLD,
  layout: {
    'icon-size': 1,
    'icon-image': ReportIconNoCamera.name,
    'icon-allow-overlap': true,
    'icon-ignore-placement': false,
  },
  paint: {
    'icon-opacity': ['get', 'opacity'],
  },
};

const iconCameraOverlayStyle: LayerProps = {
  ...iconFullStyle,
  layout: {
    ...iconFullStyle.layout,
    'icon-image': ReportIconJustCamera.name,
  },
};

const viewshedStyle: LayerProps = {
  type: 'symbol',
  minzoom: REPORTS_ICONS_SIMPLE_ZOOM_THRESHOLD,
  layout: {
    'icon-size': 1,
    'icon-image': ReportIconFov.name,
    'icon-allow-overlap': true,
    'icon-ignore-placement': false,
    'icon-rotate': ['get', 'azimuth'],
    'icon-offset': ReportIconFov.offset,
    'icon-rotation-alignment': 'map',
  },
  paint: {
    'icon-opacity': ['get', 'opacity'],
  },
  filter: ['>', 'azimuth', 0],
};

const toGeojsonFeature = (input: {
  report: Report | ReportPhoto;
  isFadable: boolean;
  geoEventType?: GeoEventType;
}): Feature<Geometry, ReportFeatureProperties> | null => {
  const { report, isFadable, geoEventType } = input;
  const { media, dateCreated } = report;

  // get first element if media is an array
  const relevantMedia = Array.isArray(media) ? media[0] : media;

  if (!relevantMedia) {
    return null;
  }

  const opacity = getOpacityForReportMarker(
    dateCreated,
    isFadable,
    getReportMapVisibilityHours(
      'geoEventType' in report ? report.geoEventType : geoEventType,
    ),
  );

  const azimuth = relevantMedia?.az || 0;

  if (!hasValidCoordinates(relevantMedia)) {
    return null;
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [relevantMedia.lng, relevantMedia.lat],
    },
    properties: {
      id: report.id,
      geoEventId: report.geoEventId,
      opacity,
      azimuth,
    },
  };
};

export const reportClickHandler = (
  geoJsonFeatures: MapboxFeature<ReportFeatureProperties>[],
  location: Location,
  geoEventType: ReportableGeoEventType,
): void => {
  const feature = geoJsonFeatures[0];
  if (!feature) return;

  const { geoEventId, id } = feature.properties;
  history.push(
    getReportDetailPath({ geoEventType, geoEventId, reportId: id }),
    {
      /**
       * Add `fromRoute` state when navigating to a report page.
       * Use case: opening a report detail page from the wildfire
       * route as a full‑screen dialog, so closing it returns the user
       * to the same map position and context.
       */
      fromRoute: location.pathname + location.search,
    },
  );
};

export const ReportsLayer = (props: ReportsLayerProps): JSX.Element | null => {
  const { reports, isFadable = true, interactive = true, geoEventType } = props;

  const geojson: FeatureCollection<Geometry, ReportFeatureProperties> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: reports
        .map((report) => toGeojsonFeature({ report, isFadable, geoEventType }))
        .filter(
          (feature) => feature && feature.properties.opacity > 0,
        ) as Feature<Geometry, ReportFeatureProperties>[],
    }),
    [reports, isFadable, geoEventType],
  );

  const obfuscatedReports = useMemo(
    () => reports.filter(isRingReport),
    [reports],
  );

  return (
    <>
      <Source id={REPORTS_SOURCE_ID} type="geojson" data={geojson}>
        <Layer
          id={REPORTS_VIEWSHED_LAYER_ID}
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          {...viewshedStyle}
        />
        <Layer
          id={REPORTS_ICONS_LAYER_ID}
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          {...iconSimpleStyle}
          metadata={{ interactive }}
        />
        <Layer
          id={REPORTS_ICONS_FULL_LAYER_ID}
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          {...iconFullStyle}
          metadata={{ interactive }}
        />
        <Layer
          id={`${REPORTS_ICONS_LAYER_ID}_cams`}
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          {...iconCameraOverlayStyle}
        />
      </Source>
      {obfuscatedReports.length > 0 && (
        <ReportObfuscationLayer
          beforeId={REPORTS_VIEWSHED_LAYER_ID}
          reports={obfuscatedReports}
          sourceId={REPORTS_OBFUSCATION_LAYER_ID}
          isFadable={isFadable}
          geoEventType={geoEventType}
        />
      )}
    </>
  );
};
